import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2, XCircle } from "lucide-react";

const SYMS = ["NIFTY", "BANKNIFTY", "SENSEX"];
const CHUNK = 2 * 1024 * 1024;

export const QLImport = () => {
  const fileRef = useRef(null);
  const [kind, setKind] = useState("underlying");
  const [sym, setSym] = useState("NIFTY");
  const [dayfirst, setDayfirst] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadInfo, setUploadInfo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [committing, setCommitting] = useState(false);
  const [batches, setBatches] = useState([]);

  const loadBatches = () => api.get("/quantlab/import/batches").then((r) => setBatches(r.data.items || [])).catch(() => {});
  useEffect(() => {
    loadBatches();
    const id = setInterval(loadBatches, 5000);
    return () => clearInterval(id);
  }, []);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose a CSV or Excel file first.");
    setUploading(true); setPreview(null); setUploadInfo(null); setProgress(0);
    try {
      const mpHdr = { headers: { "Content-Type": "multipart/form-data" } };
      const fd0 = new FormData();
      fd0.append("filename", f.name);
      const { data: start } = await api.post("/quantlab/import/start", fd0, mpHdr);
      const total = Math.ceil(f.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", start.upload_id);
        fd.append("index", String(i));
        fd.append("file", f.slice(i * CHUNK, (i + 1) * CHUNK), `chunk_${i}`);
        await api.post("/quantlab/import/chunk", fd, mpHdr);
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      const fdf = new FormData();
      fdf.append("upload_id", start.upload_id);
      const { data: fin } = await api.post("/quantlab/import/finalize", fdf, mpHdr);
      setUploadInfo(fin);
      const { data: pv } = await api.get(`/quantlab/import/preview?upload_id=${start.upload_id}&kind=${kind}`);
      setPreview(pv);
      setMapping(pv.suggested_mapping || {});
      toast.success(`Uploaded ${f.name} (${(fin.size_bytes / 1048576).toFixed(1)} MB) — map the columns below.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const commit = async () => {
    const missing = (preview?.required || []).filter((r) => !mapping[r]);
    if (missing.length) return toast.error(`Map required fields: ${missing.join(", ")}`);
    if (!mapping.timestamp && !mapping.date) return toast.error("Map a timestamp column (or date + time).");
    setCommitting(true);
    try {
      await api.post("/quantlab/import/commit", {
        upload_id: uploadInfo.upload_id, kind, symbol: sym, exchange: sym === "SENSEX" ? "BSE" : "NSE",
        mapping, dayfirst,
      });
      toast.success("Import started — validation running in background.");
      setPreview(null); setUploadInfo(null);
      if (fileRef.current) fileRef.current.value = "";
      loadBatches();
    } catch (e) { toast.error(e?.response?.data?.detail || "Commit failed"); }
    finally { setCommitting(false); }
  };

  return (
    <div className="space-y-5" data-testid="ql-import">
      <div className="border border-zinc-800 bg-[#0a0a0a] p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Upload historical data · CSV / Excel · timestamps treated as IST</div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="text-[10px] font-mono text-zinc-500">
            <div className="mb-1">DATA KIND</div>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="bg-black border border-zinc-800 px-2 py-2 text-white" data-testid="ql-import-kind">
              <option value="underlying">Underlying OHLC</option>
              <option value="option">Option contracts OHLC</option>
            </select>
          </div>
          <div className="text-[10px] font-mono text-zinc-500">
            <div className="mb-1">INSTRUMENT</div>
            <select value={sym} onChange={(e) => setSym(e.target.value)} className="bg-black border border-zinc-800 px-2 py-2 text-white" data-testid="ql-import-symbol">
              {SYMS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-[10px] font-mono text-zinc-400 pb-2 cursor-pointer">
            <input type="checkbox" checked={dayfirst} onChange={(e) => setDayfirst(e.target.checked)} className="accent-[#22C55E]" data-testid="ql-import-dayfirst" />
            DD-MM-YYYY dates (dayfirst)
          </label>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="text-xs font-mono text-zinc-400 file:bg-zinc-800 file:border-0 file:text-white file:px-3 file:py-2 file:mr-3" data-testid="ql-import-file" />
          <button onClick={upload} disabled={uploading}
                  className="flex items-center gap-2 bg-[#22C55E] hover:bg-[#4ade80] disabled:opacity-50 text-black text-[10px] uppercase tracking-widest font-mono font-bold px-4 py-2.5"
                  data-testid="ql-import-upload">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {uploading ? `Uploading ${progress}%` : "Upload & preview"}
          </button>
        </div>
      </div>

      {preview && (
        <div className="border border-zinc-800 bg-[#0a0a0a] p-4 space-y-4" data-testid="ql-import-mapping">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Column mapping · {uploadInfo?.filename}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {preview.fields.map((f) => (
              <div key={f} className="text-[10px] font-mono">
                <div className={`mb-1 uppercase ${preview.required.includes(f) ? "text-[#22C55E]" : "text-zinc-500"}`}>
                  {f}{preview.required.includes(f) && " *"}
                </div>
                <select value={mapping[f] || ""} onChange={(e) => setMapping({ ...mapping, [f]: e.target.value || undefined })}
                        className="w-full bg-black border border-zinc-800 px-2 py-1.5 text-white" data-testid={`ql-map-${f}`}>
                  <option value="">— not mapped —</option>
                  {preview.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto border border-zinc-900">
            <table className="w-full text-[10px] font-mono">
              <thead><tr className="text-zinc-500 border-b border-zinc-900">{preview.columns.map((c) => <th key={c} className="px-2 py-1.5 text-left whitespace-nowrap">{c}</th>)}</tr></thead>
              <tbody>{preview.sample_rows.map((r, i) => (
                <tr key={i} className="border-b border-zinc-900/50 text-zinc-400">
                  {preview.columns.map((c) => <td key={c} className="px-2 py-1 whitespace-nowrap">{String(r[c] ?? "")}</td>)}
                </tr>))}
              </tbody>
            </table>
          </div>
          <button onClick={commit} disabled={committing}
                  className="bg-[#007AFF] hover:bg-[#3395FF] disabled:opacity-50 text-white text-[10px] uppercase tracking-widest font-mono font-bold px-5 py-2.5"
                  data-testid="ql-import-commit">
            {committing ? "Starting…" : "Validate & import"}
          </button>
        </div>
      )}

      <div className="border border-zinc-800 bg-[#0a0a0a]">
        <div className="px-4 py-3 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Import batches</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead><tr className="text-zinc-500 text-[10px] uppercase border-b border-zinc-900">
              {["Batch", "Kind", "Symbol", "Status", "Rows", "Inserted", "Duplicates", "Rejected", "Warnings", "Started"].map((h) => <th key={h} className="px-3 py-2 text-left">{h}</th>)}
            </tr></thead>
            <tbody>
              {batches.length === 0 && <tr><td colSpan={10} className="px-3 py-6 text-center text-zinc-600">No imports yet.</td></tr>}
              {batches.map((b) => (
                <tr key={b.batch_id} className="border-b border-zinc-900/50" data-testid={`ql-batch-${b.batch_id}`}>
                  <td className="px-3 py-2 text-zinc-400">{b.batch_id}</td>
                  <td className="px-3 py-2">{b.kind}</td>
                  <td className="px-3 py-2 font-bold">{b.symbol}</td>
                  <td className="px-3 py-2">
                    {b.status === "processing" ? <span className="text-[#F59E0B] flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />processing</span>
                      : b.status === "done" ? <span className="text-[#22C55E] flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />done</span>
                        : <span className="text-red-400 flex items-center gap-1" title={b.error}><XCircle className="h-3 w-3" />failed</span>}
                  </td>
                  <td className="px-3 py-2">{b.rows_total ?? "—"}</td>
                  <td className="px-3 py-2 text-[#22C55E]">{b.rows_inserted ?? "—"}</td>
                  <td className="px-3 py-2 text-zinc-500">{b.rows_duplicate ?? "—"}</td>
                  <td className="px-3 py-2 text-red-400">{b.rows_rejected ?? "—"}</td>
                  <td className="px-3 py-2 text-[#F59E0B]">{b.warnings ?? "—"}</td>
                  <td className="px-3 py-2 text-zinc-500 whitespace-nowrap">{b.started_at ? new Date(b.started_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QLImport;
